import type { ColumnFiltersState } from '@tanstack/react-table'
import {
  FILTER_ID_TO_DATA_KEY,
  mapColumnFiltersToRolodexFilters,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFilters'
import type { RolodexHistogramFilter, RolodexHistogramQuery } from 'types'
import { RolodexEntryType, RolodexFilterOp } from 'types'
import { z } from 'zod'

export const FACET_FILTER_IDS = ['vendor', 'docType'] as const
export type FacetFilterId = (typeof FACET_FILTER_IDS)[number]

const FacetHistogramRowSchema = z.looseObject({
  value: z.string().optional(),
  count: z.number().optional(),
  n: z.number().optional(),
})
const FacetValueSchema = z
  .string()
  .min(1, { error: 'Facet value must be a non-empty string' })

export const FacetOptionSchema = z.looseObject({
  value: FacetValueSchema,
  count: z.number({ error: 'Facet count must be a number' }),
})
export type FacetOption = z.infer<typeof FacetOptionSchema>

function toHistogramTags(
  tags: Record<string, string | string[]>
): Record<string, string[]> {
  const histogramTags: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(tags)) {
    histogramTags[key] = typeof value === 'string' ? [value] : value
  }
  return histogramTags
}

function toHistogramFilters(
  filters: ReturnType<typeof mapColumnFiltersToRolodexFilters>
): RolodexHistogramFilter[] {
  const histogramFilters: RolodexHistogramFilter[] = []
  for (const filter of filters) {
    if (
      filter.op === RolodexFilterOp.REGEX &&
      typeof filter.value === 'string'
    ) {
      histogramFilters.push({
        field: filter.field,
        key: filter.key,
        op: '~',
        value: filter.value,
      })
      continue
    }
    if (filter.op === RolodexFilterOp.IN && Array.isArray(filter.value)) {
      const values = filter.value.filter(
        (item): item is string | number =>
          typeof item === 'string' || typeof item === 'number'
      )
      if (values.length === 0) continue
      histogramFilters.push({
        field: filter.field,
        key: filter.key,
        op: 'in',
        value: values,
      })
    }
  }
  return histogramFilters
}

export function buildDocumentBrowseFacetQueries(
  scopedTags: Record<string, string | string[]> | undefined,
  activeFilters: ColumnFiltersState | undefined
): RolodexHistogramQuery[] | undefined {
  if (!scopedTags) return undefined
  return FACET_FILTER_IDS.map((filterId) => {
    const dataKey = FILTER_ID_TO_DATA_KEY[filterId]
    const otherFilters = (activeFilters ?? []).filter(
      ({ id }) => id !== filterId
    )
    return {
      type: RolodexEntryType.SYSTEM_DOCUMENT,
      tags: toHistogramTags(scopedTags),
      select: [
        { field: 'data', key: dataKey, group_by: true, label: 'value' },
        { field: 'id', op: 'count', label: 'count' },
      ],
      filters: toHistogramFilters(
        mapColumnFiltersToRolodexFilters(otherFilters)
      ),
      limit: 10000,
    }
  })
}

function parseFacetHistogramRow(
  row: unknown,
  dataKey: string
): FacetOption | undefined {
  const parsedRow = FacetHistogramRowSchema.safeParse(row)
  if (!parsedRow.success) return undefined
  const labeledValue = FacetValueSchema.safeParse(parsedRow.data.value)
  const keyedValue = FacetValueSchema.safeParse(parsedRow.data[dataKey])
  const parsedOption = FacetOptionSchema.safeParse({
    value: labeledValue.success
      ? labeledValue.data
      : keyedValue.success
        ? keyedValue.data
        : undefined,
    count: parsedRow.data.count ?? parsedRow.data.n,
  })
  return parsedOption.success ? parsedOption.data : undefined
}

export function parseDocumentBrowseFacetOptions(
  data: unknown
): Partial<Record<FacetFilterId, FacetOption[]>> | undefined {
  if (data === undefined) return undefined
  if (!Array.isArray(data)) return undefined
  const result: Partial<Record<FacetFilterId, FacetOption[]>> = {}
  FACET_FILTER_IDS.forEach((filterId, index) => {
    const rows = data[index]
    if (!Array.isArray(rows)) return
    const dataKey = FILTER_ID_TO_DATA_KEY[filterId]
    result[filterId] = rows
      .flatMap((row) => {
        const option = parseFacetHistogramRow(row, dataKey)
        return option === undefined ? [] : [option]
      })
      .sort((a, b) => a.value.localeCompare(b.value))
  })
  return result
}

export function facetOptionsToColumnValues(
  optionsByFilterId: Partial<Record<FacetFilterId, FacetOption[]>>
): Partial<Record<FacetFilterId, string[]>> {
  const values: Partial<Record<FacetFilterId, string[]>> = {}
  for (const filterId of FACET_FILTER_IDS) {
    const options = optionsByFilterId[filterId]
    if (!options) continue
    values[filterId] = options.map((option) => option.value)
  }
  return values
}
